import { SelectionHeader } from '@/components/bulkQueue/cells/SelectionHeader';
import {
    useAssignmentClinicianRec,
    useBulkClinicianContext,
    useCanSelectClinicianVisit,
    useIsClinicianSelected,
    useSelectedClinician,
} from '@/components/bulkQueue/tables/clinician/BulkClinicianContext';
import {
    VisitAppointmentRowFragment,
    VisitRequestAppointmentRowFragment,
} from '@/components/bulkQueue/tables/queries.gql.types';
import { CellProps, HeaderProps, VisitRequestCellProps } from '@/components/bulkQueue/types';
import { ClinicianChip } from '@/components/clinicians/ClinicianChip';
import { AssignmentClinicianDecisionSupport } from '@/components/decisionSupport/AssignmentClinicianDecisionSupport';
import { LegacyClinicianDecisionSupport } from '@/components/decisionSupport/LegacyClinicianDecisionSupportTrigger';
import {
    VisitRecommendedCliniciansBaseFragment,
    VisitRequestRecommendedCliniciansBaseFragment,
} from '@/components/decisionSupport/queries/clinicianRecommendations.gql.types';
import { findClinicianRecommendation } from '@/components/decisionSupport/utils/clinicianRecommendations.utils';
import {
    DecisionSupportSelection,
    pickTopRanked,
    selectionCaption,
} from '@/components/decisionSupport/utils/decisionSupport.utils';
import { useAxleRecommended } from '@/components/decisionSupport/utils/decisionSupport.utils';
import { CaptionLabel } from '@/components/labels/CaptionLabel';
import { NoneLabel } from '@/components/labels/NoneLabel';
import { HorizontalStack, VerticalStack } from '@/components/utils/linearLayouts';
import { Checkbox } from '@mui/material';
import { Dictionary } from 'lodash';
import { FC, useCallback, useMemo } from 'react';

type Appointment = {
    id: string;
};

type LegacyAppointmentRecommendationsProps = {
    isLoading: boolean;
    allRecommendations: Dictionary<
        VisitRequestRecommendedCliniciansBaseFragment | VisitRecommendedCliniciansBaseFragment
    >;
    appointment: Appointment;
    selectedId: string | null;
    setAppointmentSelection(appointmentId: string, clinicianId: string | null): void;
};

const LegacyAppointmentRecommendations: FC<LegacyAppointmentRecommendationsProps> = (props) => {
    const { isLoading, selectedId, setAppointmentSelection, allRecommendations, appointment } =
        props;
    const recommendations =
        typeof allRecommendations[appointment.id] === 'undefined'
            ? null
            : allRecommendations[appointment.id];
    const recommendedClinicians = useMemo(
        () => recommendations?.clinicianRecommendations ?? [],
        [recommendations],
    );

    const selectedFragment = useMemo(
        () => findClinicianRecommendation(recommendedClinicians, selectedId),
        [recommendedClinicians, selectedId],
    );

    const selectedSelection: DecisionSupportSelection | null = useMemo(
        () =>
            selectedFragment
                ? {
                      id: selectedFragment.clinician.id,
                      label: selectedFragment.clinician.fullName,
                  }
                : null,
        [selectedFragment],
    );
    const topRecommendation = useAxleRecommended(recommendedClinicians);

    const isSelectionDifferentFromTop =
        selectedSelection != null &&
        topRecommendation != null &&
        selectedSelection.id !== topRecommendation.clinician.id;

    const setSelectedClinician = useCallback(
        (selection: DecisionSupportSelection | null) => {
            setAppointmentSelection(appointment.id, selection?.id ?? null);
        },
        [appointment.id, setAppointmentSelection],
    );

    return (
        <VerticalStack key={appointment.id}>
            <CaptionLabel fontSize={'0.8em'}>
                {isSelectionDifferentFromTop ? 'Selected' : 'Recommended'}
            </CaptionLabel>
            <HorizontalStack spacing={1} sx={{ justifyContent: 'space-between' }}>
                {selectedFragment ? (
                    <>
                        <ClinicianChip clinician={selectedFragment.clinician} showLink={false} />
                        {recommendations ? (
                            <LegacyClinicianDecisionSupport
                                selectedClinician={selectedSelection}
                                setSelectedClinician={setSelectedClinician}
                                recommendations={recommendations.clinicianRecommendations}
                            />
                        ) : null}
                    </>
                ) : (
                    <NoneLabel text={isLoading ? 'Loading...' : 'None'} fontSize={'14px'} />
                )}
            </HorizontalStack>
        </VerticalStack>
    );
};

type ClinicianSelectionCellBaseProps = {
    isLoading: boolean;
    isSelected: boolean;
    setIsSelected: (id: string, isSelected: boolean) => void;
    isInFlight: boolean;
    disabled?: boolean;
};

type ClinicianSelectionCellProps = ClinicianSelectionCellBaseProps & {
    recommendations?: Dictionary<
        VisitRequestRecommendedCliniciansBaseFragment | VisitRecommendedCliniciansBaseFragment
    >;
    selectedVisitAppointmentClinician?: Map<string, string>;
    setSelectedVisitAppointmentClinician(
        visitId: string,
        appointmentId: string,
        clinicianId: string | null,
    ): void;
};

export const ClinicianVisitRequestSelectionCell: FC<
    VisitRequestCellProps<ClinicianSelectionCellProps>
> = (props) => {
    const {
        selectedVisitAppointmentClinician,
        setSelectedVisitAppointmentClinician,
        entity: { id, appointments },
        recommendations: providedRecommendations,
        isLoading,
        isSelected,
        setIsSelected,
        isInFlight,
        disabled = false,
    } = props;

    const recommendations = useMemo(() => providedRecommendations ?? {}, [providedRecommendations]);

    const hasRecommendations = useMemo(
        () =>
            Object.values(recommendations).some(
                ({ clinicianRecommendations }) => clinicianRecommendations.length > 0,
            ),
        [recommendations],
    );

    const setAppointmentSelection = useCallback(
        (appointmentId: string, clinicianId: string | null) => {
            setSelectedVisitAppointmentClinician(id, appointmentId, clinicianId);
        },
        [setSelectedVisitAppointmentClinician, id],
    );

    return (
        <HorizontalStack
            justifyContent='space-between'
            onClick={(event) => {
                // Prevent the row from being clicked.
                event.stopPropagation();
            }}
        >
            <VerticalStack>
                {appointments.length > 0 ? (
                    appointments.map((appointment) => (
                        <LegacyAppointmentRecommendations
                            key={appointment.id}
                            setAppointmentSelection={setAppointmentSelection}
                            selectedId={
                                selectedVisitAppointmentClinician?.get(appointment.id) ?? null
                            }
                            isLoading={isLoading}
                            allRecommendations={recommendations}
                            appointment={appointment}
                        />
                    ))
                ) : (
                    <NoneLabel />
                )}
            </VerticalStack>
            {hasRecommendations && (
                <Checkbox
                    size='small'
                    disabled={isLoading || isInFlight || disabled}
                    checked={isSelected}
                    onChange={(event) => {
                        event.stopPropagation();
                        setIsSelected(id, !isSelected);
                    }}
                />
            )}
        </HorizontalStack>
    );
};

type LegacyClinicianVisitRequestSelectionCellProps = ClinicianSelectionCellProps & {
    visitRequest: { id: string; appointments: { id: string }[] };
};

export const LegacyClinicianVisitRequestSelectionCell: FC<
    LegacyClinicianVisitRequestSelectionCellProps
> = ({ visitRequest, ...rest }) => {
    // ClinicianVisitRequestSelectionCell only uses id and appointments from entity
    return (
        <ClinicianVisitRequestSelectionCell
            entity={visitRequest as VisitRequestCellProps['entity']}
            {...rest}
        />
    );
};

type AppointmentRecommendationsProps = {
    entityId: string;
    appointment: VisitRequestAppointmentRowFragment | VisitAppointmentRowFragment;
};

// DS visitAssignment flow: clinician recommendations come per-visit from the
// new query; selection still flows through the shared context selection store.
const AssignmentAppointmentRecommendations: FC<AppointmentRecommendationsProps> = (props) => {
    const { entityId, appointment } = props;
    const { state } = useBulkClinicianContext();
    const { definitions, clinicianRecommendation } = useAssignmentClinicianRec(entityId);
    const [selectedClinician, setSelectedClinician] = useSelectedClinician(
        entityId,
        appointment.id,
    );

    const topRecommendation = useMemo(
        () => pickTopRanked(clinicianRecommendation?.recommendations),
        [clinicianRecommendation],
    );

    // The store holds only the user's pick.
    // fall back to the top rec.
    const resolvedClinician =
        selectedClinician ??
        (topRecommendation
            ? { id: topRecommendation.clinician.id, label: topRecommendation.clinician.fullName }
            : null);

    return (
        <VerticalStack key={appointment.id}>
            <CaptionLabel fontSize={'0.8em'}>
                {selectionCaption(selectedClinician != null)}
            </CaptionLabel>
            <HorizontalStack spacing={1} alignItems='center'>
                {resolvedClinician ? (
                    resolvedClinician.label
                ) : (
                    <NoneLabel
                        text={state === 'loading' ? 'Loading...' : 'None'}
                        fontSize={'14px'}
                    />
                )}
                <AssignmentClinicianDecisionSupport
                    definitions={definitions}
                    clinicianRecommendation={clinicianRecommendation}
                    selectedClinician={resolvedClinician}
                    setSelectedClinician={setSelectedClinician}
                    disabled={state === 'submitting'}
                />
            </HorizontalStack>
        </VerticalStack>
    );
};

export const ClinicianSelectionCell: FC<CellProps<{ isLocked?: boolean }>> = (props) => {
    const {
        entity: { id, appointments },
        isLocked = false,
    } = props;

    const { state } = useBulkClinicianContext();
    const [isSelected, setIsSelected] = useIsClinicianSelected(id);
    const canSelect = useCanSelectClinicianVisit(id);

    return (
        <HorizontalStack
            justifyContent='space-between'
            onClick={(event) => {
                event.stopPropagation();
            }}
            sx={isLocked ? { opacity: 0.4, pointerEvents: 'none' } : undefined}
        >
            <VerticalStack>
                {appointments && appointments.length > 0 ? (
                    appointments.map((appointment) => (
                        <AssignmentAppointmentRecommendations
                            key={appointment.id}
                            entityId={id}
                            appointment={appointment}
                        />
                    ))
                ) : (
                    <NoneLabel />
                )}
            </VerticalStack>
            {canSelect && !isLocked && (
                <Checkbox
                    size='small'
                    disabled={state !== 'idle'}
                    checked={isSelected}
                    onChange={(event) => {
                        event.stopPropagation();
                        setIsSelected(!isSelected);
                    }}
                />
            )}
        </HorizontalStack>
    );
};

export const ClinicianSelectionHeader: FC<
    HeaderProps & { isLocked?: boolean; hasEditableItems?: boolean }
> = (props) => {
    const { isLocked, hasEditableItems, ...rest } = props;
    const { entityCount, summary, state, selectedCount, submit, selectableCount, selectAll } =
        useBulkClinicianContext();

    if (isLocked) {
        return (
            <SelectionHeader
                {...rest}
                hasItems={false}
                isDisabled={true}
                numSelected={0}
                summaryString={undefined}
                onApply={submit}
            />
        );
    }

    return (
        <SelectionHeader
            {...rest}
            hasItems={entityCount > 0}
            isLoading={state === 'loading'}
            isSubmitting={state === 'submitting'}
            isDisabled={state !== 'idle' || selectedCount === 0}
            numSelected={selectedCount}
            summaryString={summary}
            onApply={submit}
            selectableCount={selectableCount}
            onSelectAll={selectAll}
            hasEditableItems={hasEditableItems}
        />
    );
};
